import type { ENUM_VALUES } from '../../platform/analytics/vocabulary'
import type { LocalDate } from '../../platform/localDate'
import type { Category } from '../categories/categoryTree'
import type { BudgetPeriod, Envelope } from './budgetWire'

/**
 * Display rules. No money arithmetic: the only arithmetic here is on counts,
 * dates, and the server's own percentage.
 */

export type BudgetFilter = (typeof ENUM_VALUES.budget_filter)[number]

export const BUDGET_FILTERS: readonly { value: BudgetFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'over', label: 'Over' },
  { value: 'close', label: 'Close' },
  { value: 'untouched', label: 'Untouched' },
]

export const isOver = (e: Envelope): boolean => e.progress.isOverBudget
/** E2: the server's flags, never recomputed. `is_alert` is true when over, too. */
export const isClose = (e: Envelope): boolean => e.progress.isAlert && !e.progress.isOverBudget
/** E3 */
export const isUntouched = (e: Envelope): boolean => e.progress.spentCents === 0

export function matches(f: BudgetFilter, e: Envelope): boolean {
  switch (f) {
    case 'all':
      return true
    case 'over':
      return isOver(e)
    case 'close':
      return isClose(e)
    case 'untouched':
      return isUntouched(e)
  }
}

export function splitActive(all: readonly Envelope[]): { active: Envelope[]; paused: Envelope[] } {
  return { active: all.filter((e) => e.isActive), paused: all.filter((e) => !e.isActive) }
}

export function filterCounts(active: readonly Envelope[]): Record<BudgetFilter, number> {
  const counts: Record<BudgetFilter, number> = { all: 0, over: 0, close: 0, untouched: 0 }
  for (const e of active) {
    for (const { value } of BUDGET_FILTERS) if (matches(value, e)) counts[value] += 1
  }
  return counts
}

export const shownPct = (e: Envelope): number => Math.round(e.progress.percentage)

export function reason(e: Envelope): string | null {
  if (isOver(e)) return `${shownPct(e)}% · over`
  if (isClose(e)) return 'Close to limit'
  if (isUntouched(e)) return 'Nothing spent yet'
  return null
}

export function periodLabel(p: BudgetPeriod): string | null {
  if (p === 'weekly') return 'Weekly'
  if (p === 'yearly') return 'Yearly'
  return null
}

export type MonthHeader = { monthLabel: string; remainingDays: number }

export function monthHeader(today: LocalDate): MonthHeader {
  const [y = 0, m = 1, d = 1] = today.split('-').map(Number)
  const monthLabel = new Intl.DateTimeFormat('en-CA', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, 1)))
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate()
  return { monthLabel, remainingDays: daysInMonth - d }
}

export function headerLine(today: LocalDate): string {
  const { monthLabel, remainingDays } = monthHeader(today)
  const left = remainingDays === 0 ? 'last day' : `${remainingDays} ${remainingDays === 1 ? 'day' : 'days'} left`
  return `${monthLabel} · ${left} · envelopes reset on the 1st`
}

/** One level of parentage (categories are two-deep). */
function overlaps(a: string, b: string, categories: readonly Category[]): boolean {
  if (a === b) return true
  const parentOf = (id: string) => categories.find((c) => c.id === id)?.parent_id ?? null
  return parentOf(a) === b || parentOf(b) === a
}

export function overlapWarning(
  categoryId: string | null,
  editingId: string | null,
  active: readonly Envelope[],
  categories: readonly Category[],
): string | null {
  if (categoryId === null) return null
  const hit = active.find(
    (e) => e.id !== editingId && e.categoryId !== null && overlaps(categoryId, e.categoryId, categories),
  )
  return hit ? `Spending in ${hit.name} is also counted by your ${hit.name} envelope.` : null
}

export type PeriodBounds = { start: LocalDate; end: LocalDate }

const DATE = /^(\d{4})-(\d{2})-(\d{2})$/

function parts(date: LocalDate): [number, number, number] {
  const m = DATE.exec(date)
  if (m === null) throw new Error(`budget period: not a YYYY-MM-DD date: ${JSON.stringify(date)}`)
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])]
  const probe = new Date(Date.UTC(y, mo - 1, d))
  if (probe.getUTCMonth() !== mo - 1 || probe.getUTCDate() !== d) throw new Error(`budget period: no such date: ${date}`)
  return [y, mo, d]
}

const iso = (ms: number): LocalDate => new Date(ms).toISOString().slice(0, 10)
const DAY = 86_400_000

/**
 * The period the server counts spending in, from the LOCAL date (A5): weekly Monday to Sunday,
 * monthly the calendar month, yearly the calendar year. Date arithmetic only, in UTC so no
 * local offset can move a day. Malformed input throws.
 */
export function periodBounds(period: BudgetPeriod, today: LocalDate): PeriodBounds {
  const [y, m, d] = parts(today)
  if (period === 'monthly') {
    return { start: iso(Date.UTC(y, m - 1, 1)), end: iso(Date.UTC(y, m, 0)) }
  }
  if (period === 'yearly') return { start: `${y}-01-01`, end: `${y}-12-31` }
  const day = Date.UTC(y, m - 1, d)
  const sinceMonday = (new Date(day).getUTCDay() + 6) % 7
  const monday = day - sinceMonday * DAY
  return { start: iso(monday), end: iso(monday + 6 * DAY) }
}

/** Whole days from `today` to the period end; 0 is the last day. */
export function daysLeft(bounds: PeriodBounds, today: LocalDate): number {
  const [ey, em, ed] = parts(bounds.end)
  const [ty, tm, td] = parts(today)
  return Math.round((Date.UTC(ey, em - 1, ed) - Date.UTC(ty, tm - 1, td)) / DAY)
}

const fmt = (opts: Intl.DateTimeFormatOptions, date: LocalDate): string => {
  const [y, m, d] = parts(date)
  return new Intl.DateTimeFormat('en-CA', { ...opts, timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, d)))
}

/** "1 – 31 Oct 2026", or across a month or year "28 Dec 2026 – 3 Jan 2027". */
export function periodRange(bounds: PeriodBounds): string {
  const [sy, sm] = parts(bounds.start)
  const [ey, em] = parts(bounds.end)
  const day = (date: LocalDate) => fmt({ day: 'numeric' }, date)
  const mon = (date: LocalDate) => fmt({ month: 'short' }, date)
  if (sy === ey && sm === em) return `${day(bounds.start)} – ${day(bounds.end)} ${mon(bounds.end)} ${ey}`
  return `${day(bounds.start)} ${mon(bounds.start)} ${sy} – ${day(bounds.end)} ${mon(bounds.end)} ${ey}`
}

export type EnvelopeStatus = 'on_track' | 'close' | 'over' | 'untouched' | 'paused'

/**
 * The ONE place the server's flags become a skin: over > close > spent. The row bar, the column and the list row
 * all draw from this, and `envelopeStatus` extends it, so they cannot disagree. Pause is not its concern.
 */
export type EnvelopeSkin = 'over' | 'close' | 'spent'
export function envelopeSkin(e: Envelope): EnvelopeSkin {
  if (isOver(e)) return 'over'
  if (isClose(e)) return 'close'
  return 'spent'
}

/** The `Meter` skin an envelope state wears: `close` is the warn fill. */
export const meterSkin = (s: EnvelopeSkin): 'over' | 'warn' | 'spent' => (s === 'close' ? 'warn' : s)

/** Paused wins; otherwise the skin, with an untouched envelope told apart from an on-track one. */
export function envelopeStatus(e: Envelope): EnvelopeStatus {
  if (!e.isActive) return 'paused'
  const skin = envelopeSkin(e)
  if (skin !== 'spent') return skin
  return isUntouched(e) ? 'untouched' : 'on_track'
}
